import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useToggle } from 'react-use';
import { useHistory } from 'react-router';
import map from 'lodash/map';

import { makeFetchEntityByIdSelector } from '../../../selectors/fetch';
import useGettingCalendar from './useGettingCalendar';
import useGettingCalendars from './useGettingCalendars';
import useRenameCalendar from './useRenameCalendar';
import useCopyCalendar from './useCopyCalendar';
import useDeleteCalendar from './useDeleteCalendar';
import useDeleteCalendarDevice from './useDeleteCalendarDevice';
import useAddDeviceToCalendar from './useAddDeviceToCalendar';
import useGettingDevices from '../../DevicesPage/hooks/useGettingDevices';
import useAddPlaylist from './useAddPlaylist';
import useDeletePlaylistFromCalendar from './useDeletePlaylistFromCalendar';
import useChangeIntervals from './useChangeIntervals';
import useDevicesFilter from '../../DevicesPage/hooks/useDevicesFilter';
import pathToRegexp from 'path-to-regexp';
import { Path } from '../../../routes';
import first from 'lodash/first';
import useGettingPlaylistContents from '../../PlaylistPage/hooks/useGettingPlaylistContents';

const useCalendarPageLogic = () => {
  const history = useHistory();
  const [showDevices, toggleDevices] = useToggle(false);
  const { response: playlists } = useSelector(
    makeFetchEntityByIdSelector('playlists')
  );
  const [editPlaylistId, setEditPlaylistId] = useState(null);
  const [showPreview, togglePreview] = useToggle(false);
  const [isOverModal, setOverModal] = useState(false);
  const { calendar = {}, onFetchCalendar } = useGettingCalendar();
  const onSuccess = () => {
    onFetchCalendar();
    onFetchCalendars();
  };
  const { onFetchCalendars } = useGettingCalendars();
  const { onRenameCalendar } = useRenameCalendar({
    onSuccess,
  });
  const { onCopyCalendar } = useCopyCalendar({ onSuccess });
  const { onDeleteCalendar } = useDeleteCalendar({
    onSuccess: () => {
      onFetchCalendars({
        onSuccess: (calendars) =>
          history.push(
            pathToRegexp.compile(Path.CALENDAR)({ id: first(calendars).id })
          ),
      });
    },
  });
  const { onAddDeviceToCalendar } = useAddDeviceToCalendar({
    calendar,
    onFetchCalendar,
  });
  const { onDeleteCalendarDevice } = useDeleteCalendarDevice({
    calendar,
    onFetchCalendar,
  });
  const { onAddPlaylistToCalendar } = useAddPlaylist({
    calendar,
    onSuccess: onFetchCalendar,
  });
  const { devices: allDevices, onFetchDevices } = useGettingDevices();

  const onDeleteAllDevices = () =>
    map(calendar.players, ({ id }) => onDeleteCalendarDevice(id)());

  const { onDeletePlaylistFromCalendar } = useDeletePlaylistFromCalendar({
    calendar,
    onSuccess: onFetchCalendar,
  });
  const { onChangeIntervals } = useChangeIntervals({
    calendar,
    onSuccess: onFetchCalendar,
  });
  const { onSearch, filters, onFilter, filter } = useDevicesFilter();
  const { contents, onFetchContents } = useGettingPlaylistContents();

  const onTogglePreview = (slot) => {
    const id = slot ? slot.id : null;
    setEditPlaylistId(id);
    togglePreview();
  };

  return {
    editPlaylistId,
    contents,
    showPreview,
    calendar,
    showDevices,
    toggleDevices,
    allDevices,
    playlists,
    filters,
    togglePreview: onTogglePreview,
    isOverModal,
    setOverModal,
    onSearch,
    onFilter,
    filter,
    onFetchContents,
    onFetchCalendar,
    onFetchCalendars,
    onRenameCalendar,
    onCopyCalendar,
    onDeleteCalendar,
    onDeleteCalendarDevice,
    onAddDeviceToCalendar,
    onFetchDevices,
    onDeleteAllDevices,
    onAddPlaylistToCalendar,
    onDeletePlaylistFromCalendar,
    onChangeIntervals,
  };
};

export default useCalendarPageLogic;
